import { createTheme } from '@mui/material/styles';
import { DEV_NIGHT_DARK_MUI, type DevNightPaletteId } from './devNightPalettes';
import { brandButtons, brandButtonsDark } from './brandButtons';

const fontStack =
  '"Lexend Deca", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif';

const sharedTypography = {
  fontFamily: fontStack,
  button: {
    textTransform: 'none' as const,
    fontWeight: 500,
  },
};

const sharedShape = { borderRadius: 8 };

function createButtonOverrides(mode: 'light' | 'dark') {
  const secondary =
    mode === 'dark' ? brandButtonsDark.secondary : brandButtons.secondary;

  return {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: brandButtons.shape.pillRadius,
          textTransform: 'none' as const,
          fontWeight: 500,
          boxShadow: 'none',
        },
        contained: {
          boxShadow: brandButtons.primary.shadow,
          '&:hover': {
            boxShadow: brandButtons.primary.shadowHover,
          },
        },
        containedPrimary: {
          backgroundColor: brandButtons.primary.main,
          color: brandButtons.primary.contrast,
          '&:hover': {
            backgroundColor: brandButtons.primary.hover,
          },
        },
        outlined: {
          borderWidth: '1px',
          borderColor: secondary.border,
          color: secondary.text,
          backgroundColor: secondary.bg,
          '&:hover': {
            borderWidth: '1px',
            borderColor: secondary.hoverBorder,
            backgroundColor: secondary.hoverBg,
            color: secondary.text,
          },
        },
      },
    },
  };
}

const sharedComponents = {
  ...createButtonOverrides('light'),
  MuiTextField: {
    defaultProps: {
      variant: 'outlined' as const,
      size: 'small' as const,
    },
  },
};

/**
 * Valeurs hex/rgba du mode nuit — **alignées** sur
 * `src/styles/dev-night-mode-variables.css` / `dev-night-mode-palettes.ts`.
 * MUI ne supporte pas `var()` dans `createTheme` (augmentColor / lighten).
 */

/** Thème clair (production / défaut) */
export function createLightAppTheme() {
  return createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: brandButtons.primary.main,
        light: '#c49a7c',
        dark: brandButtons.primary.hover,
        contrastText: brandButtons.primary.contrast,
      },
      secondary: {
        main: '#F6C674',
        light: '#fce8b8',
        dark: '#e6b85c',
        contrastText: '#3d2918',
      },
      error: {
        main: '#dc2626',
        light: '#fecaca',
        dark: '#b91c1c',
      },
      warning: { main: '#f59e0b' },
      info: { main: '#A5765C' },
      success: { main: '#16a34a' },
      background: {
        default: '#f7f4f0',
        paper: '#ffffff',
      },
      text: {
        primary: '#111827',
        secondary: '#6b7280',
      },
    },
    typography: sharedTypography,
    shape: sharedShape,
    components: sharedComponents,
  });
}

/**
 * Thème MUI sombre (dev) — couleurs = `DEV_NIGHT_DARK_MUI[paletteId]`.
 */
export function createDarkAppTheme(paletteId: DevNightPaletteId = 'default') {
  const D = DEV_NIGHT_DARK_MUI[paletteId];
  return createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: D.brandPrimary,
        light: D.brandPrimaryHover,
        dark: '#8B5E47',
        contrastText: '#1a1410',
      },
      secondary: {
        main: D.brandGold,
        light: '#f5e6b8',
        dark: '#c4a43a',
        contrastText: '#1a1410',
      },
      error: {
        main: '#f87171',
        light: '#fecaca',
        dark: '#b91c1c',
      },
      warning: { main: '#fbbf24' },
      info: { main: D.brandPrimary },
      success: { main: '#4ade80' },
      background: {
        default: D.canvas,
        paper: D.surfaceElevated,
      },
      text: {
        primary: D.textPrimary,
        secondary: D.textSecondary,
      },
      divider: D.divider,
    },
    typography: sharedTypography,
    shape: sharedShape,
    components: {
      ...sharedComponents,
      ...createButtonOverrides('dark'),
    },
  });
}

export function createAppTheme(mode: 'light' | 'dark', paletteId: DevNightPaletteId = 'default') {
  return mode === 'dark' ? createDarkAppTheme(paletteId) : createLightAppTheme();
}
